<script setup lang="ts">
import { useRootStore } from '@Shared/stores/root';
import { getRawValue } from '@Shared/api/portal';
import { StyleValue } from 'vue';
import { Permission } from '@Shared/types/Permission';

interface CleanedFieldProps {
  recordId: string;
  emptyValueText: string;
  smartDataModuleId: string;
  href?: string;
  iconSize?: number;
  textStyling?: StyleValue;
  value?: string;
  isFieldCleaned: boolean;
  keyField: string;
  tooltipFontSize?: number;
}

const props = defineProps<CleanedFieldProps>();
const { recordId, emptyValueText, smartDataModuleId, value, isFieldCleaned, keyField } = toRefs(props);
const isDisplayingRawValue = defineModel({ default: false });

const root = useRootStore();

enum CleanedType {
  /// Hiding verbatim text due to profanity being found in it
  Profanity = 0,
  /// Hiding verbatim text due to names being found in it
  NameMasking = 1,
  /// Email/Phone hiding due to sensitivity
  Sensitive = 2,
}

function GetTextForCleanedType(cleanedType: CleanedType) {
  switch (cleanedType) {
    case CleanedType.Profanity: {
      return 'offensive language';
    }
    case CleanedType.NameMasking: {
      return 'name mentioned';
    }
    case CleanedType.Sensitive: {
      return 'sensitive information';
    }
  }
}

const cleanedMappings: Record<string, CleanedType[]> = {
  // Legacy term, still used by Westpac in their custom solution
  redacted_profanity: [CleanedType.Profanity],
  locale_profanity_hidden: [CleanedType.Profanity],
  locale_profanity_name_hidden: [
    // order matters (since we render the text in this order)
    CleanedType.Profanity,
    CleanedType.NameMasking,
  ],
  locale_name_hidden: [CleanedType.NameMasking],
  locale_sensitive_hidden: [CleanedType.Sensitive],
};

const currentMappings = computed<CleanedType[]>(() =>
  value.value && cleanedMappings[value.value] ? cleanedMappings[value.value] : []
);
const isCleaned = computed(() => isFieldCleaned.value && currentMappings.value.length > 0);

const displayIcon = computed<string | null>(() => {
  if (
    currentMappings.value.includes(CleanedType.Profanity) ||
    currentMappings.value.includes(CleanedType.NameMasking)
  ) {
    return 'i-mdi-minus-circle';
  } else if (currentMappings.value.includes(CleanedType.Sensitive)) {
    return 'i-mdi-eye';
  } else {
    return null;
  }
});

const displayValue = ref<string | null>(null);
watchEffect(() => {
  if (!isCleaned.value && value) {
    displayValue.value = !value.value || value.value == '-' ? emptyValueText.value : value.value;
    isDisplayingRawValue.value = true;
  }
});

watch([recordId], () => {
  isDisplayingRawValue.value = false;
});

const canViewRawValue = computed<boolean>(() => {
  if (!root.currentUser) return false;

  for (const mapping of currentMappings.value) {
    // default to thet profanity permission but for sensitive lookup sensitive specifically
    let permission = Permission.DataMasking_Read_ProfanityAndName;
    if (mapping == CleanedType.Sensitive) {
      permission = Permission.DataMasking_Read_Sensitive;
    }
    if (!root.currentUser.permissions?.includes(permission)) {
      return false;
    }
  }

  return true;
});

const toggleVisibility = async () => {
  if (!canViewRawValue.value || !isCleaned.value || !value.value) return;

  const rawValue = await getRawValue(smartDataModuleId.value, keyField.value, recordId.value);

  displayValue.value = !rawValue || rawValue == '-' ? emptyValueText.value : rawValue;
  isDisplayingRawValue.value = true;
};

onMounted(() => {
  // If we mount the component and isDisplayingRawValue is true, then it means that the user clicked to display the raw value before but the component was remounted,
  // so we have to load the raw value again.
  if (isDisplayingRawValue.value && isCleaned.value) toggleVisibility();
});

const tooltipMessage = computed<string | null>(() => {
  if (currentMappings.value.length == 0) return null;

  let message = 'Hidden due to ' + currentMappings.value.map(GetTextForCleanedType).join(' or ') + '.';

  if (canViewRawValue.value) {
    message += ' Click to show.';
  }

  return message;
});

const link = computed<string>(() => {
  // if we pass in a link then we need to determine if we need to append the value or not to the link (if it's mailto or tel)
  if (props.href && displayValue.value != 'No link provided') {
    if (props.href === 'mailto:' || props.href === 'tel:') {
      return props.href + displayValue.value!;
    }
    return displayValue.value!;
  }
  return '';
});
</script>

<template>
  <div
    v-if="isCleaned && displayIcon && !isDisplayingRawValue"
    class="min-w-0 relative"
    :style="{
      height: (iconSize ?? 22) + 'px',
      width: (iconSize ?? 22) + 'px',
    }"
  >
    <span class="c-#c73362 cursor-pointer group/cleaned inline-block" @click="toggleVisibility">
      <span
        class="absolute top-0"
        :style="{
          width: (iconSize ?? 22) + 'px',
          height: (iconSize ?? 22) + 'px',
        }"
        :class="[displayIcon]"
        :id="'cleanedFieldsIcon' + recordId"
      ></span>
      <span
        v-if="isCleaned"
        class="max-w-400px clean-field-tooltip w-max whitespace-normal"
        :style="{
          lineHeight: (tooltipFontSize ?? 12) + 'px',
          fontSize: (tooltipFontSize ?? 12) + 'px',
        }"
        :class="[
          // Opacity so we can animate it
          // we increase opacity when hovering over the parent (the group)
          // but we hide it when hovering over the popup (need important due to ordering of css)
          'invisible opacity-0 group-hover/cleaned:visible group-hover/cleaned:opacity-100 hover:opacity-0!',
        ]"
      >
        {{ tooltipMessage }}
      </span>
    </span>
  </div>
  <a
    v-else
    class="m-0 text-3.5 inline-block decoration-none w-full"
    :style="textStyling"
    :href="href && link && link != '' ? link : undefined"
    target="_blank"
    :class="{
      'text-4 italic': displayValue === emptyValueText,
      'color-[#0563C1] underline! hover:color-[#0563C1] break-all': href && displayValue != emptyValueText,
    }"
  >
    {{ displayValue }}
  </a>
</template>

<style lang="scss">
.clean-field-tooltip {
  transition: opacity 0.5s;
  cursor: initial;
  background: #f3f3f3;
  border: 1px solid #ddd;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  position: absolute;
  z-index: 100;
  border-radius: 5px;
  top: 125%;
  left: 0;
  margin-left: -6px;
  font-family: 'Open Sans';
  font-size: 12px;
  line-height: 12px;
  color: #444 !important;
  padding: 6px;

  &::before {
    content: '';
    height: 12px;
    width: 24px;
    position: absolute;
    background: #f3f3f3;
    left: 0;
    top: 0;
    z-index: -1;
    border-radius: 3px;
  }

  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
    top: -4px;
    left: 8px;
    background: #f3f3f3;
    transform: rotate(45deg);
    z-index: -2;
    border-left: 1px solid #ddd;
    border-top: 1px solid #ddd;
  }
}
</style>
